// src/PropertyView.js
// Vista principal de propiedad para compradores invitados (REFACTORIZADO - 7 ARCHIVOS)

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { getPropertyIcon } from './constants/propertyIcons';
import { getPropertyStatus } from './utils/propertyHelpers';
import { useTranslation } from 'react-i18next';

// Hooks personalizados
import { usePropertyState } from './hooks/usePropertyState';
import { usePropertyBidding } from './hooks/usePropertyBidding';
import { useEngagementTracking } from './hooks/useEngagementTracking';
import { usePropertyTranslations } from './hooks/usePropertyTranslations';

// Componentes
import NotificationToast from './components/NotificationToast';
import PropertyHeader from './components/PropertyHeader';
import PropertyContent from './components/PropertyContent';
import ScheduleVisitModal from './components/ScheduleVisitModal';
import { ConfirmBidModal } from './components/PinModals';

// ✅ NUEVO: Imports para handleSaveProfile
import { createBuyerProfile, updateBuyerProfile, getBrowserMetadata, generateSessionId, countVerifiedBlindOffers } from './services/supabaseService';

const PropertyView = () => {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Estado local de UI
  const [notification, setNotification] = useState(null);
  const [showScheduleVisitModal, setShowScheduleVisitModal] = useState(false);
  const [blindOffersCount, setBlindOffersCount] = useState(0);

  // Función helper para notificaciones
  const showNotification = (type, title, message, options = {}) => {
    setNotification({ type, title, message, persistent: options.persistent });
    
    if (!options.persistent) {
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Hook 1: Estado y datos de la propiedad
  const {
    property,
    invite,
    propertyBids,
    propertyInvites,
    availableConditions,
    buyerProfile,
    userBlindOffer,
    loading,
    error,
    refreshBids,
    refreshInvite,
    refreshBuyerProfile,
    setInvite,
  } = usePropertyState(propertyId, token);

  // Hook 2: Engagement tracking
  const { trackEvent } = useEngagementTracking(
    propertyId,
    token,
    !!property && !!invite
  );

  // Hook 4: Lógica de ofertas (usa trackEvent)
  const {
    bidAmount, setBidAmount,
    confirmAmount, setConfirmAmount,
    validationError, setValidationError,
    showConfirmModal, setShowConfirmModal,
    openConfirmModal,
    selectedConditions, setSelectedConditions,
    conditionsText, setConditionsText,
    handleBidSubmit,
    handleConfirmBid,
    handleBlindBidSubmit,
} = usePropertyBidding(
  property,
  invite,
  propertyBids,
  propertyInvites,
  refreshBids,
  showNotification,
  trackEvent
);

  // ============================================
  // ✅ NUEVO: Cargar contador de ofertas ciegas verificadas
  // ============================================
  useEffect(() => {
    const loadBlindOffersCount = async () => {
      const propertyStatus = property ? getPropertyStatus(property) : null;
      const isBlindActive = propertyStatus === 'BLIND_ACTIVE';

      if (!isBlindActive || !property?.id) return;

      try {
        const count = await countVerifiedBlindOffers(property.id);
        setBlindOffersCount(count);
      } catch (error) {
        console.error('Error cargando contador de ofertas:', error);
      }
    };

    loadBlindOffersCount();
  }, [property?.id, property?.biddingStartDate, userBlindOffer]);

  // ============================================
  // ✅ NUEVO: Handler centralizado para guardar perfil
  // ============================================
  const handleSaveProfile = async (profileData) => {
    try {
      if (!invite?.email || !property?.id || !invite?.token) {
        throw new Error('Faltan datos necesarios para guardar el perfil');
      }

      const metadata = getBrowserMetadata();
      const sessionId = generateSessionId();

      const completeProfileData = {
        ...profileData,
        propertyId: property.id,
        inviteToken: invite.token,
        userEmail: invite.email,
        metadata,
        sessionId,
      };

      if (buyerProfile) {
        // Actualizar perfil existente
        await updateBuyerProfile(buyerProfile.id, profileData, {
          propertyId: property.id,
          inviteToken: invite.token,
          sessionId,
          metadata,
        });

        if (trackEvent) {
          trackEvent('buyer_profile_updated', {
            propertyId: property.id,
            hasFeatures: !!(profileData.minBedrooms || profileData.minBathrooms || profileData.desiredSqm),
            timestamp: new Date().toISOString(),
          });
        }
      } else {
        // Crear nuevo perfil
        await createBuyerProfile(completeProfileData);
        
        if (trackEvent) {
          trackEvent('buyer_profile_created', {
            propertyId: property.id,
            hasFeatures: !!(profileData.minBedrooms || profileData.minBathrooms || profileData.desiredSqm),
            timestamp: new Date().toISOString(),
          });
        }
      }

      if (refreshBuyerProfile) {
        await refreshBuyerProfile();
      }
    } catch (error) {
      console.error('Error guardando perfil:', error);
      throw error;
    }
  };

  const { t } = useTranslation();
  const { translations } = usePropertyTranslations(propertyId, property?.translationsEnabled);

  // Estados derivados
  const propertyStatus = property ? getPropertyStatus(property) : 'CLOSED';
  const isUpcoming = propertyStatus === 'UPCOMING';
  const isOpen = propertyStatus === 'OPEN';
  const isClosed = propertyStatus === 'CLOSED';
  const isLastChance = propertyStatus === 'LAST_CHANCE';

  const statusIcon = getPropertyIcon(propertyStatus);
  const StatusIcon = statusIcon.icon;

  const highestBid = propertyBids[0];
  const currentPrice = highestBid ? highestBid.amount : (property?.startPrice || 0);
  const hasBids = propertyBids.length > 0;

  const userBids = propertyBids.filter(b => b.buyerEmail === invite?.email);
  const userLastBid = userBids.length > 0 ? userBids[0] : null;
  const isTopBidder = userLastBid && highestBid && userLastBid.id === highestBid.id;

  const totalBidders = new Set(propertyBids.map(b => b.buyerEmail)).size;
  const showBidders = totalBidders >= 3;

  // Pantallas de carga y error
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
          <div className="text-white text-xl">{t('loading.title')}</div>
          <div className="text-slate-400 text-sm mt-2">{t('loading.subtitle')}</div>
        </div>
      </div>
    );
  }

  if (error === 'INVALID_INVITE' || !property || !invite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <Building2 className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('error.accessDenied')}</h2>
          <p className="text-slate-400">{t('error.invalidLink')}</p>
        </div>
      </div>
    );
  }

  // Validación de acceso: bloquear usuarios sin acceso en cualquier fase
  if (!invite?.hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <Building2 className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('error.accessDenied')}</h2>
          <p className="text-slate-400">{t('error.invalidLink')}</p>
        </div>
      </div>
    );
  }

  if (error === 'LOAD_ERROR') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-red-500/50 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <Building2 className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('error.loadError')}</h2>
          <p className="text-slate-400 mb-4">{t('error.loadErrorDesc')}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Render principal
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        
        {/* Header con título y estado */}
        <PropertyHeader
          property={property}
          propertyStatus={propertyStatus}
          statusIcon={statusIcon}
          propertyBids={propertyBids}
          totalBidders={totalBidders}
          showBidders={showBidders}
          propertyInvites={propertyInvites}
          invite={invite}
          blindOffersCount={blindOffersCount}
          translations={translations}
        />

        {/* Contenido principal: tarjetas, formulario, media, detalles */}
        <PropertyContent
          property={property}
          invite={invite}
          propertyBids={propertyBids}
          hasBids={hasBids}
          currentPrice={currentPrice}
          highestBid={highestBid}
          userLastBid={userLastBid}
          isTopBidder={isTopBidder}
          isClosed={isClosed}
          isUpcoming={isUpcoming}
          bidAmount={bidAmount}
          setBidAmount={setBidAmount}
          handleBlindBidSubmit={handleBlindBidSubmit}
          validationError={validationError}
          setValidationError={setValidationError}
          handleBidSubmit={handleBidSubmit}
          availableConditions={availableConditions}
          selectedConditions={selectedConditions}
          setSelectedConditions={setSelectedConditions}
          conditionsText={conditionsText}
          setConditionsText={setConditionsText}
          trackEvent={trackEvent}
          onScheduleVisit={() => setShowScheduleVisitModal(true)}
          buyerProfile={buyerProfile}
          refreshBuyerProfile={refreshBuyerProfile}
          onSaveProfile={handleSaveProfile}
          userBlindOffer={userBlindOffer}
          showNotification={showNotification}
          translations={translations}
        />
      </div>

      {/* Modales de PIN */}
      {showConfirmModal && (
        <ConfirmBidModal
          bidAmount={bidAmount}
          confirmAmount={confirmAmount}
          setConfirmAmount={setConfirmAmount}
          handleConfirmBid={handleConfirmBid}
          onClose={() => setShowConfirmModal(false)}
          selectedConditions={selectedConditions}
          availableConditions={availableConditions}
          conditionsText={conditionsText}
        />
      )}

      {/* Modal de solicitar visita */}
      {showScheduleVisitModal && (
        <ScheduleVisitModal
          property={property}
          invite={invite}
          buyerProfile={buyerProfile}
          onProfileCreated={refreshBuyerProfile}
          isOpen={showScheduleVisitModal}
          onClose={() => setShowScheduleVisitModal(false)}
          trackEvent={trackEvent}
        />
      )}

      {/* Notificaciones */}
      {notification && (
        <NotificationToast
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default PropertyView;